import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import styled, { ThemeContext } from 'styled-components/macro'

import Icon from 'components/atoms/icon'
import Typography from 'components/molecules/typography'
import CarfileKindSwitch from 'components/molecules/carfile-kind-switch'
import { useMediaLayout } from 'use-media'
import ToggleSwitch from 'components/atoms/toggle-switch'
import { useTranslation } from 'react-i18next'
import { media } from 'utilities/styled'

const CarLocation = styled.div`
  display: flex;
  align-items: center;
`

const Container = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizings.lvl1};
  justify-content: space-between;
  align-items: center;

  ${media.desktop`
    margin-top: ${({ theme }) => theme.sizings.lvl2};
  `}
`

const CarLocationTypography = styled(Typography)`
  font-size: ${({ theme }) => theme.sizings.lvl2};
  font-weight: 500;
`

const StyledCarfileKindSwitch = styled(CarfileKindSwitch)`
  height: 100%;
  display: flex;
  align-items: center;
  margin: 0;
  padding-left: ${({ theme, $isDesktop }) =>
    $isDesktop ? theme.sizings.lvl3 : 0};
  padding-right: ${({ theme, $isDesktop }) =>
    $isDesktop ? theme.sizings.lvl3 : 0};
`

const StyledCarLocation = styled(CarLocation)`
  margin-top: ${({ theme }) => theme.sizings.lvl2};

  ${media.desktop`
    margin-top: 0;
  `}
`

const sharedPropTypes = {
  carId: PropTypes.string.isRequired,
  location: PropTypes.string,
  kind: PropTypes.string,
  setCarKind: PropTypes.func,
  showKindSwitch: PropTypes.bool,
}

const DesktopCarLocationAndKind = ({
  location,
  kind,
  carId,
  setCarKind,
  showKindSwitch,
}) => {
  return (
    <Container>
      {location && (
        <CarLocation>
          <Icon type="location" size="lg" />
          <CarLocationTypography type="InlineBodyText">
            {location}
          </CarLocationTypography>
        </CarLocation>
      )}
      {showKindSwitch && (
        <StyledCarfileKindSwitch
          $isDesktop
          selected={kind}
          kind={kind}
          setCarKind={setCarKind}
          carId={carId}
        />
      )}
    </Container>
  )
}

DesktopCarLocationAndKind.propTypes = sharedPropTypes

const MobileCarLocationAndKind = ({
  location,
  kind,
  carId,
  setCarKind,
  showKindSwitch,
  salesView,
  toggleSalesView,
}) => {
  const theme = useContext(ThemeContext)
  const { t } = useTranslation()

  return (
    <>
      <Container>
        {showKindSwitch && (
          <StyledCarfileKindSwitch
            selected={kind}
            kind={kind}
            setCarKind={setCarKind}
            carId={carId}
          />
        )}
        <ToggleSwitch
          checked={salesView}
          onChange={toggleSalesView}
          labelLeft={t('toggleSwitch.sales')}
          trackColor={theme.colors.actionsStandard}
        />
      </Container>
      {location && (
        <StyledCarLocation>
          <Icon type="location" size="lg" />
          <CarLocationTypography type="InlineBodyText">
            {location}
          </CarLocationTypography>
        </StyledCarLocation>
      )}
    </>
  )
}

MobileCarLocationAndKind.propTypes = {
  ...sharedPropTypes,
  salesView: PropTypes.bool,
  toggleSalesView: PropTypes.func,
}

const CarLocationAndKind = ({
  location,
  kind,
  carId,
  setCarKind,
  showKindSwitch,
  salesView,
  toggleSalesView,
}) => {
  const theme = useContext(ThemeContext)
  const isDesktop = useMediaLayout({ minWidth: theme.metrics.desktop })

  return isDesktop ? (
    <DesktopCarLocationAndKind
      location={location}
      kind={kind}
      carId={carId}
      setCarKind={setCarKind}
      showKindSwitch={showKindSwitch}
    />
  ) : (
    <MobileCarLocationAndKind
      location={location}
      kind={kind}
      carId={carId}
      setCarKind={setCarKind}
      showKindSwitch={showKindSwitch}
      salesView={salesView}
      toggleSalesView={toggleSalesView}
    />
  )
}

CarLocationAndKind.propTypes = {
  ...sharedPropTypes,
  salesView: PropTypes.bool,
  toggleSalesView: PropTypes.func,
}

CarLocationAndKind.defaultProps = {
  location: '',
  kind: '',
  setCarKind: null,
  showKindSwitch: false,
}

export default CarLocationAndKind
